import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, CardBody, FormGroup, Input, Label } from 'reactstrap';
import { Loading } from 'js/components';
import OrgSnapshotChrome from './OrgSnapshotChrome';
import OrgSnapshotCombinedRadar from './OrgSnapshotCombinedRadar';
import {
  datetimeLocalNow,
  datetimeLocalToUtcIso,
  snapshotSetPath,
} from './org-snapshot-utils';
import styles from './OrgSnapshots.module.scss';

function progressLabel(progress) {
  if (!progress) {
    return 'Creating snapshot…';
  }
  const stage = progress.stage || 'scoring';
  if (stage === 'org_tree' || stage === 'waiting') {
    return 'Creating snapshot…';
  }
  if (stage === 'document') {
    return 'Resolving assessment document…';
  }
  if (stage === 'saving') {
    return 'Saving snapshot…';
  }
  if (progress.teams_total) {
    return `Scoring teams ${progress.teams_done ?? 0} of ${
      progress.teams_total
    }…`;
  }
  return 'Scoring…';
}

function generateNamePlaceholder() {
  const currentDate = new Date();
  const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
  return `e.g. ${currentDate.getFullYear()} Q${quarter} Snapshot`;
}

function OrgSnapshotCreate({
  organization,
  workspace,
  sourceName,
  template,
  liveQuestionScores,
  liveCatalog = null,
  liveLoading,
  hasScorableScores,
  creating,
  progress,
  createError,
  onAsOfChange,
  onCreateSnapshot,
}) {
  const { workspaceId, orgId, sourceType, sourceKey } = useParams();
  const decodedKey = useMemo(() => decodeURIComponent(sourceKey), [sourceKey]);
  const setPath = snapshotSetPath(workspaceId, orgId, sourceType, decodedKey);
  const namePlaceholder = useMemo(() => generateNamePlaceholder(), []);

  const [asOfLocal, setAsOfLocal] = useState(datetimeLocalNow);
  const [name, setName] = useState('');

  const endDateIso = datetimeLocalToUtcIso(asOfLocal);

  useEffect(() => {
    onAsOfChange(endDateIso);
  }, [endDateIso, onAsOfChange]);

  const futureAsOf =
    endDateIso && new Date(endDateIso).getTime() > Date.now() + 1000;
  const canCreate = hasScorableScores && !creating && !futureAsOf;

  return (
    <OrgSnapshotChrome
      workspaceId={workspaceId}
      orgId={orgId}
      organization={organization}
      workspace={workspace}
      sourceType={sourceType}
      sourceKey={decodedKey}
      extra={{
        hideTitle: true,
        currentCrumb: { label: 'Snapshots', to: setPath },
      }}
    >
      <Card>
        <CardBody>
          <h5>Create snapshot: {sourceName}</h5>
          <p className={styles.limitation}>
            Capture a snapshot of the organization's data as it would appear at
            a specific time with the current organization structure and results.
          </p>
          <div className={styles.createLayout}>
            <div className={styles.createChartCol}>
              {liveLoading && !liveQuestionScores ? (
                <Loading />
              ) : (
                <OrgSnapshotCombinedRadar
                  organization={organization}
                  assessmentTemplate={template}
                  questionScores={liveQuestionScores}
                  catalog={liveCatalog}
                  isLoading={liveLoading}
                  compact
                  hideLegend
                  mode="light"
                />
              )}
            </div>
            <div className={styles.createFieldsCol}>
              <FormGroup>
                <Label for="snapshot-name">
                  Name <span className="text-muted">(optional)</span>
                </Label>
                <Input
                  id="snapshot-name"
                  type="text"
                  value={name}
                  placeholder={namePlaceholder}
                  onChange={(e) => setName(e.target.value)}
                  disabled={creating}
                  autoFocus
                />
              </FormGroup>
              <FormGroup>
                <Label for="snapshot-at">Snapshot as of</Label>
                <Input
                  id="snapshot-at"
                  type="datetime-local"
                  value={asOfLocal}
                  max={datetimeLocalNow()}
                  onChange={(e) => setAsOfLocal(e.target.value)}
                  disabled={creating}
                />
              </FormGroup>
              {!liveLoading && !hasScorableScores ? (
                <p className="text-muted">
                  No scorable combined scores as of this time. Create is
                  disabled.
                </p>
              ) : null}
              {creating ? (
                <>
                  <div className={styles.progress}>
                    {progressLabel(progress)}
                  </div>
                  <p className={styles.limitation}>
                    You can leave this page. The snapshot will still be captured
                    in the background.
                  </p>
                </>
              ) : null}
              {createError ? (
                <p className="text-danger">{createError}</p>
              ) : null}
              <div>
                <Button
                  color="primary"
                  disabled={!canCreate}
                  onClick={() =>
                    onCreateSnapshot({ name, snapshotAt: endDateIso })
                  }
                >
                  Create
                </Button>
                <Link to={setPath} className="btn btn-secondary ml-2">
                  Cancel
                </Link>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </OrgSnapshotChrome>
  );
}

export default OrgSnapshotCreate;
